import {
  Box,
  createShapeId,
  Mat,
  Point2d,
  snapAngle,
  StateNode,
  TLClickEventInfo,
  TLDrawShapeSegment,
  TLPointerEventInfo,
  TLShapeId,
  TLShapePartial,
  toFixed,
  Vec,
  VecLike,
  VecModel,
} from "tldraw"

import { CustomHighlightShape } from "../CustomHighlightShape"
// @ts-ignore
import PolyBool from "polybooljs"

import { last, uniqueId } from "lodash"
import { STROKE_SIZES } from "../shared/default-shape-constants"
import { LDPage, LDPageMeta } from "../../shared/LDPage"
import { MarkerShape } from "../../shared/MarkerShape"
import { getPointsFromSegments, toProxyShape } from "../shapes/MarkerShapeUtil"
import { getFreehandOptions } from "../shapes/draw/getPath"
import { parseSVGPath1 } from "./MarkerDrawing"
import { svgInk } from "../shapes/shared/freehand/svgInk"
import {
  CustomDrawShape,
  CustomDrawShapeProps,
} from "../../shared/CustomDrawShape"
import { distance } from "./CustomNudge"
import { Color, Expr } from "../../shared/Expr"

type Point = { x: number; y: number }
type ZPoint = { x: number; y: number; z: number }

function distanceBetweenPoints(a: Point, b: Point): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  return Math.hypot(dx, dy) // Equivalent to sqrt(dx*dx + dy*dy)
}
function arePointsClose(a: Point, b: Point, threshold = 0.5): boolean {
  const dx = a.x - b.x
  const dy = a.y - b.y
  return dx * dx + dy * dy <= threshold * threshold
}

// Helper: Check if two segments are connected
function segmentsOverlapOrTouch(
  segA: Point[],
  segB: Point[],
  threshold = 0.5,
): boolean {
  for (const p1 of segA) {
    for (const p2 of segB) {
      if (arePointsClose(p1, p2, threshold)) {
        return true
      }
    }
  }
  return false
}
export function pointBetweenAt(a: Point, b: Point, t: number): Point {
  const vec = {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
  } as Point

  return vec
}

export function densifyLine(line: Point[], step: number = 10): Point[] {
  if (line.length < 2) return [...line]

  const result: Point[] = []

  for (let i = 0; i < line.length - 1; i++) {
    const a = line[i]
    const b = line[i + 1]
    result.push(a) // always include the start of the segment

    const segLen = distance(a, b)
    const count = Math.floor(segLen / step)

    for (let j = 1; j < count; j++) {
      const t = j / count

      result.push(pointBetweenAt(a, b, t))
    }
  }

  result.push(line[line.length - 1]) // include last point

  return result
}
// Main: Group connected segments
function pointInPolygon(point: Point, polygon: Point[]): boolean {
  let inside = false
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x,
      yi = polygon[i].y
    const xj = polygon[j].x,
      yj = polygon[j].y

    const intersect =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi
    if (intersect) inside = !inside
  }
  return inside
}

function shapeContainsSegment(container: Point[], segment: Point[]): boolean {
  return segment.every((pt) => pointInPolygon(pt, container))
}

function groupConnectedSegments(
  segments: Point[][],
  threshold = 0.5,
): Point[][][] {
  const groups: Point[][][] = []
  const visited = new Set<number>()

  for (let i = 0; i < segments.length; i++) {
    if (visited.has(i)) continue

    const group: Point[][] = []
    const stack = [i]

    while (stack.length) {
      const current = stack.pop()!
      if (visited.has(current)) continue

      visited.add(current)
      group.push(segments[current])

      for (let j = 0; j < segments.length; j++) {
        if (visited.has(j)) continue

        const segA = segments[current]
        const segB = segments[j]

        if (
          segmentsOverlapOrTouch(segA, segB, threshold) ||
          shapeContainsSegment(segA, segB) ||
          shapeContainsSegment(segB, segA)
        ) {
          stack.push(j)
        }
      }
    }

    groups.push(group)
  }

  return groups
}

function getThickLineRect(a: Point, b: Point, thickness: number): Point[] {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const len = Math.hypot(dx, dy)
  if (len === 0) return []

  // Unit normal vector (perpendicular to the line)
  const nx = -(dy / len)
  const ny = dx / len
  const r = thickness / 2

  return [
    { x: a.x + nx * r, y: a.y + ny * r }, // top-left
    { x: a.x - nx * r, y: a.y - ny * r }, // bottom-left
    { x: b.x - nx * r, y: b.y - ny * r }, // bottom-right
    { x: b.x + nx * r, y: b.y + ny * r }, // top-right
    { x: a.x + nx * r, y: a.y + ny * r }, // close the path
  ]
}

function splitLineByErasedPoints(
  line: Point[],
  erasedPoints: Point[],
): Point[][] {
  const result: Point[][] = []
  const erasedSet = new Set(erasedPoints.map((p) => `${p.x},${p.y}`))
  let currentSegment: Point[] = []

  for (const pt of line) {
    if (erasedSet.has(`${pt.x},${pt.y}`)) {
      // End current segment if it has points
      if (currentSegment.length > 0) {
        result.push(currentSegment)
        currentSegment = []
      }
    } else {
      currentSegment.push(pt)
    }
  }

  // Push the last segment if it wasn't closed by an erased point
  if (currentSegment.length > 0) {
    result.push(currentSegment)
  }

  return result
}
function createCirclePolygon(
  center: Point,
  radius: number,
  numPoints: number = 10,
): Point[] {
  const { x: cx, y: cy } = center
  const points: Point[] = []

  for (let i = 0; i < numPoints; i++) {
    const angle = (i / numPoints) * 2 * Math.PI
    points.push({
      x: cx + Math.cos(angle) * radius,
      y: cy + Math.sin(angle) * radius,
    })
  }

  // Optionally close the loop by repeating the first point
  points.push({ ...points[0] })

  return points
}

export function closePolygon(points: Point[]): Point[] {
  return points[0].x === points.at(-1)!.x && points[0].y === points.at(-1)!.y
    ? points
    : [...points, { ...points[0] }]
}

function isPointNearBounds(point: Point, radius: number, bounds: Box): boolean {
  const eraserMinX = point.x - radius
  const eraserMaxX = point.x + radius
  const eraserMinY = point.y - radius
  const eraserMaxY = point.y + radius

  return (
    bounds.maxX >= eraserMinX &&
    bounds.minX <= eraserMaxX &&
    bounds.maxY >= eraserMinY &&
    bounds.minY <= eraserMaxY
  )
}
function subtractPolygons(
  polyA: Point[][], // multiple contours/regions
  polyB: Point[], // single contour
): Point[][] {
  // Convert input to PolyBool format
  const polygonA = {
    regions: polyA.map((contour) => contour.map((p) => [p.x, p.y])),
    inverted: false,
  }

  const polygonB = {
    regions: [polyB.map((p) => [p.x, p.y])],
    inverted: false,
  }

  // Perform polygon difference
  const result = PolyBool.difference(polygonA, polygonB)

  // Convert back to Point[][]
  return result.regions.map((region: [number, number][]) =>
    region.map(([x, y]) => ({ x, y })),
  )
}

export class EraserErasing extends StateNode {
  static override id = "eraser-erasing"

  page = this.editor.getCurrentPage() as LDPage

  radius = this.page.meta.eraserRadius ?? 10
  info = {} as TLPointerEventInfo

  initialShape?: MarkerShape

  override shapeType = "marker-shape"

  util = this.editor.getShapeUtil(this.shapeType)

  isPen = false
  isPenOrStylus = false

  segmentMode = "free" as
    | "free"
    | "straight"
    | "starting_straight"
    | "starting_free"

  didJustShiftClickToExtendPreviousShapeLine = false

  pagePointWhereCurrentSegmentChanged = {} as Vec

  pagePointWhereNextSegmentChanged = null as Vec | null

  lastRecordedPoint = {} as Vec
  mergeNextPoint = false
  currentLineLength = 0

  markId = null as null | string

  override onEnter(info: TLPointerEventInfo) {
    const page = this.editor.getCurrentPage() as LDPage

    this.radius = page.meta.eraserRadius ?? 10

    this.markId = null
    this.info = info
    this.lastRecordedPoint = this.editor.inputs.currentPagePoint.clone()
  }
  override onPointerUp(info: TLPointerEventInfo): void {
    this.editor.markHistoryStoppingPoint()
    this.parent.transition("eraser-idle", info)
  }

  override onPointerMove(info: TLPointerEventInfo): void {
    const { inputs } = this.editor
    const unscaledPoint = { x: info.point.x, y: info.point.y }
    const mousePoint = this.editor.screenToPage(unscaledPoint)

    const circleShape = createCirclePolygon(mousePoint, this.radius * 2.5, 20)
    const eraseShape = PolyBool.union(
      {
        regions: [circleShape.map((p) => [p.x, p.y])],
        inverted: false,
      },
      {
        regions: [
          getThickLineRect(
            this.lastRecordedPoint,
            mousePoint,
            this.radius * 5,
          ).map((p) => [p.x, p.y]),
        ],
        inverted: false,
      },
    )
    const selectedLayer = (this.editor.getCurrentPage() as LDPage).meta
      .currentLayer
    const page = this.editor.getCurrentPage() as LDPage
    const pageFillShapes = this.editor
      .getCurrentPageShapes()
      .filter((shape) => !shape.isLocked)
      .filter((shape) => {
        return page.meta.onlySelectedLayer
          ? (shape.props as CustomDrawShapeProps).layer === selectedLayer
          : true
      })
      .filter((shape) => shape.type === "custom-draw")
      .filter((shape) => {
        return (shape.props as CustomDrawShapeProps).isFill
      })
      .filter((shape) => {
        const shapeGeometry = this.editor.getShapeUtil(shape).getGeometry(shape)
        const bounds = shapeGeometry.bounds
        const scaledBounds = new Box(
          bounds.minX + shape.x,
          bounds.minY + shape.y,
          bounds.maxX + shape.x,
          bounds.maxY + shape.y,
        )
        return isPointNearBounds(mousePoint, this.radius, scaledBounds)
      })

    pageFillShapes.forEach((shape2) => {
      const newPath = subtractPolygons(
        (shape2.props as CustomDrawShapeProps).segments.map((segment) =>
          closePolygon(
            segment.points.map((point: Point) => {
              return { x: point.x + shape2.x, y: point.y + shape2.y }
            }),
          ),
        ),

        closePolygon(
          eraseShape.regions[0].map((p: Point[]) => ({ x: p[0], y: p[1] })),
        ),
      )
        .map((points) => {
          return points.map((point: Point) => {
            // Convert points back to the shape's local coordinate space

            return { x: point.x - shape2.x, y: point.y - shape2.y }
          })
        })
        .map((segment) => {
          return { type: "free", points: segment }
        })
      const shapeGroups = groupConnectedSegments(
        newPath
          .map((segment) => segment.points)
          .filter((points) => points.length > 5),
      )
      shapeGroups.forEach((group) => {
        const shapeId = createShapeId()
        const segments = group.map((points) => ({
          type: "free" as const,
          points: points,
        }))

        this.editor.createShape<CustomDrawShape>({
          id: shapeId,
          x: shape2.x,
          y: shape2.y,
          type: "custom-draw",
          index: pageFillShapes.sort()[pageFillShapes.length - 1].index,
          props: {
            isFill: true,

            // strokeWidth: (shape2.props as { strokeWidth: Expr<number> })
            //   .strokeWidth,
            segments: segments,
            isComplete: true,
            isPen: false,
            scale: 1,
            layer: (shape2.props as CustomDrawShapeProps).layer,
            parameterValues: {
              ...(shape2 as CustomDrawShape).props.parameterValues,
              visible: Expr.lit(true),
              fillColor: (
                shape2.props as { isFill: true } & CustomDrawShapeProps
              ).parameterValues.fillColor,
            },
          },
        })
      })
      this.editor.deleteBindings(
        this.editor.getBindingsInvolvingShape(shape2.id),
      )

      this.editor.deleteShape(shape2.id)
    })
    this.lastRecordedPoint = mousePoint
    const pageStrokeShapes = this.editor
      .getCurrentPageShapes()
      .filter((shape) => !shape.isLocked)
      .filter((shape) => {
        return page.meta.onlySelectedLayer
          ? (shape.props as CustomDrawShapeProps).layer === selectedLayer
          : true
      })
      .filter((shape) => shape.type === "custom-draw")
      .filter((shape) => {
        return !(shape.props as CustomDrawShapeProps).isFill
      }) as (CustomDrawShape & { props: { isFill: false } })[]
    pageStrokeShapes.forEach((shape2) => {
      const points = (shape2.props as CustomDrawShapeProps).segments
        .map((segment) =>
          densifyLine(
            segment.points.map((point: Point) => {
              return {
                x: point.x + shape2.x,
                y: point.y + shape2.y,
              }
            }),
            10,
          ),
        )
        .flat()
      const pointsToRemove = points.filter((point) => {
        return distanceBetweenPoints(point, mousePoint) <= this.radius * 2.5
      })

      const newPath = splitLineByErasedPoints(points, pointsToRemove)
      const color = shape2.props.parameterValues.strokeColor

      this.editor.deleteShape(shape2.id)
      newPath.forEach((segmentPoints) => {
        const shapeId = createShapeId()
        const segment: TLDrawShapeSegment = {
          type: "free",
          points: segmentPoints,
        }
        this.editor.createShape<CustomDrawShape>({
          id: shapeId,
          type: "custom-draw",
          props: {
            isFill: false,
            segments: [segment],
            isComplete: true,
            isPen: false,
            scale: 1,
            layer: shape2.props.layer,
            transform: shape2.props.transform, // Add the missing transform property
            parameterValues: {
              ...(shape2.props as CustomDrawShapeProps).parameterValues,
              visible: Expr.lit(true),
              strokeColor: shape2.props.parameterValues.strokeColor,
              strokeWidth: shape2.props.parameterValues.strokeWidth,
            },
          },
        })
      })
    })
  }

  complete() {
    this.parent.transition("eraser-idle")
  }

  cancel() {
    // alvaro: reference 'custom-idle' instead of 'idle'
    this.parent.transition("eraser-idle", this.info)
  }
}
